// ============================================================
// CineSync — User Activity Logging Service (Cloud Firestore)
//
// Manages privacy-conscious user activity logging:
//   1. User Profiles (users/{uid})
//   2. Login Sessions (users/{uid}/sessions/{sessionId})
//   3. Watch History (users/{uid}/watchHistory/{watchId})
//   4. Room Activity (users/{uid}/roomActivity/{activityId})
//
// Privacy & Resilience Guarantees:
//   - No IP addresses, device fingerprints, or precise locations collected.
//   - No credentials, tokens, or passwords stored.
//   - All operations are completely non-blocking: errors or offline states
//     fail silently without crashing or interrupting playback/WebRTC.
//   - Designed for plug-and-play invocation from future auth/playback events.
// ============================================================

import {
  doc,
  collection,
  setDoc,
  updateDoc,
  serverTimestamp,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { db } from '@/lib/firebase';

// ── Types & Interfaces ───────────────────────────────────────

export interface BasicDeviceInfo {
  browser: string;
  os: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
}

export interface UserProfileData {
  uid: string;
  email: string | null;
  displayName: string | null;
  createdAt?: FieldValue | Timestamp;
  updatedAt?: FieldValue | Timestamp;
  deviceInfo?: BasicDeviceInfo;
}

export interface LoginSessionData {
  sessionId: string;
  loginAt: FieldValue | Timestamp;
  logoutAt: FieldValue | Timestamp | null;
  authMethod: 'google' | 'password' | string;
  browser: string;
  os: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
}

export interface WatchHistoryData {
  title: string;
  mediaType: 'local' | 'youtube' | string;
  watchStart: FieldValue | Timestamp;
  watchEnd: FieldValue | Timestamp | null;
  totalDuration: number; // seconds watched
  roomId: string | null;
}

export interface RoomActivityData {
  roomId: string;
  role: 'host' | 'participant';
  joinedAt: FieldValue | Timestamp;
  leftAt: FieldValue | Timestamp | null;
}

// ── Coarse Device Information Helper (Privacy-Conscious) ──────

/**
 * Extracts high-level, coarse device information from userAgent.
 * Collects zero persistent fingerprints, zero hardware IDs, and zero location data.
 */
export function getBasicDeviceInfo(): BasicDeviceInfo {
  if (typeof navigator === 'undefined') {
    return { browser: 'Unknown', os: 'Unknown', deviceType: 'desktop' };
  }

  const ua = navigator.userAgent || '';

  // Coarse OS detection
  let os = 'Unknown OS';
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Macintosh|Mac OS X/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';

  // Coarse Browser detection
  let browser = 'Unknown Browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome\//i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Safari';

  // Device form factor
  let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (/Tablet|iPad/i.test(ua)) {
    deviceType = 'tablet';
  } else if (/Mobi|Android|iPhone/i.test(ua)) {
    deviceType = 'mobile';
  }

  return { browser, os, deviceType };
}

// ── 1. User Profiles ─────────────────────────────────────────

/**
 * Creates or updates a base user profile document under `users/{uid}`.
 * Only collects UID, email, display name, and coarse device info.
 */
export async function upsertUserProfile(
  uid: string,
  profile: { email?: string | null; displayName?: string | null }
): Promise<boolean> {
  if (!uid) return false;

  try {
    const userRef = doc(db, 'users', uid);
    const deviceInfo = getBasicDeviceInfo();

    await setDoc(
      userRef,
      {
        uid,
        email: profile.email ?? null,
        displayName: profile.displayName ?? null,
        updatedAt: serverTimestamp(),
        deviceInfo,
        // Only set createdAt on initial document creation
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
    return true;
  } catch (error) {
    console.warn('[ActivityLogger] Failed to upsert user profile:', error);
    return false;
  }
}

// ── 2. Login Sessions ────────────────────────────────────────

/**
 * Records a new login session under `users/{uid}/sessions/{sessionId}`.
 * Generates a unique session ID and captures login timestamp and coarse device metadata.
 * Returns the generated sessionId for later correlation on logout.
 */
export async function createLoginSession(
  uid: string,
  authMethod: 'google' | 'password' | string = 'password',
  customSessionId?: string
): Promise<string | null> {
  if (!uid) return null;

  try {
    const sessionId = customSessionId || nanoid(16);
    const sessionRef = doc(db, 'users', uid, 'sessions', sessionId);
    const { browser, os, deviceType } = getBasicDeviceInfo();

    const sessionData: LoginSessionData = {
      sessionId,
      loginAt: serverTimestamp(),
      logoutAt: null,
      authMethod,
      browser,
      os,
      deviceType,
    };

    await setDoc(sessionRef, sessionData);
    return sessionId;
  } catch (error) {
    console.warn('[ActivityLogger] Failed to record login session:', error);
    return null;
  }
}

/**
 * Updates an active login session with a logout timestamp under `users/{uid}/sessions/{sessionId}`.
 */
export async function endLoginSession(
  uid: string,
  sessionId: string
): Promise<boolean> {
  if (!uid || !sessionId) return false;

  try {
    const sessionRef = doc(db, 'users', uid, 'sessions', sessionId);
    await updateDoc(sessionRef, {
      logoutAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.warn('[ActivityLogger] Failed to update logout timestamp:', error);
    return false;
  }
}

// ── 3. Watch History ─────────────────────────────────────────

/**
 * Records the start of a movie watching session under `users/{uid}/watchHistory/{watchId}`.
 * Call only when the user actually begins viewing/playing a video.
 * Returns watchId for closing or updating duration when finished.
 */
export async function startWatchRecord(
  uid: string,
  data: {
    title: string;
    mediaType?: 'local' | 'youtube' | string;
    roomId?: string | null;
    totalDuration?: number;
    customWatchId?: string;
  }
): Promise<string | null> {
  if (!uid || !data.title) return null;

  try {
    const watchId = data.customWatchId || nanoid(16);
    const watchRef = doc(db, 'users', uid, 'watchHistory', watchId);

    const record: WatchHistoryData = {
      title: data.title,
      mediaType: data.mediaType || 'local',
      watchStart: serverTimestamp(),
      watchEnd: null,
      totalDuration: data.totalDuration ?? 0,
      roomId: data.roomId ?? null,
    };

    await setDoc(watchRef, record);
    return watchId;
  } catch (error) {
    console.warn('[ActivityLogger] Failed to start watch history record:', error);
    return null;
  }
}

/**
 * Updates an active watch history record with watchEnd timestamp and total duration watched in seconds.
 */
export async function endWatchRecord(
  uid: string,
  watchId: string,
  totalDurationSeconds?: number
): Promise<boolean> {
  if (!uid || !watchId) return false;

  try {
    const watchRef = doc(db, 'users', uid, 'watchHistory', watchId);
    const updatePayload: Record<string, unknown> = {
      watchEnd: serverTimestamp(),
    };

    if (typeof totalDurationSeconds === 'number' && totalDurationSeconds >= 0) {
      updatePayload.totalDuration = Math.round(totalDurationSeconds);
    }

    await updateDoc(watchRef, updatePayload);
    return true;
  } catch (error) {
    console.warn('[ActivityLogger] Failed to finalize watch history record:', error);
    return false;
  }
}

// ── 4. Room Activity ─────────────────────────────────────────

/**
 * Records a participant or host joining a watch room under `users/{uid}/roomActivity/{activityId}`.
 * Returns activityId for subsequent update when the user leaves the room.
 */
export async function logRoomJoin(
  uid: string,
  roomId: string,
  role: 'host' | 'participant' = 'participant',
  customActivityId?: string
): Promise<string | null> {
  if (!uid || !roomId) return null;

  try {
    const activityId = customActivityId || nanoid(16);
    const activityRef = doc(db, 'users', uid, 'roomActivity', activityId);

    const record: RoomActivityData = {
      roomId,
      role,
      joinedAt: serverTimestamp(),
      leftAt: null,
    };

    await setDoc(activityRef, record);
    return activityId;
  } catch (error) {
    console.warn('[ActivityLogger] Failed to log room join activity:', error);
    return null;
  }
}

/**
 * Updates a room activity record with the leave timestamp under `users/{uid}/roomActivity/{activityId}`.
 */
export async function logRoomLeave(
  uid: string,
  activityId: string
): Promise<boolean> {
  if (!uid || !activityId) return false;

  try {
    const activityRef = doc(db, 'users', uid, 'roomActivity', activityId);
    await updateDoc(activityRef, {
      leftAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.warn('[ActivityLogger] Failed to log room leave activity:', error);
    return false;
  }
}

